// src/pages/Dashboard.tsx
import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Tabs,
  Tab,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChurchIcon from '@mui/icons-material/Church';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from '../hooks/useAuth';
import { Eglise } from '../types/auth';
import CalendarView from '../components/CalendarView';
import { getCurrentDate } from '../services/calendarService';
import { useTranslation } from 'react-i18next';
import authService from '../services/authService';
import TabPanel from '../components/common/TabPanel';

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { authState, getUserEglises } = useAuth();
  const [eglises, setEglises] = useState<Eglise[]>([]);
  const [loadingEglises, setLoadingEglises] = useState(true);
  const [errorEglises, setErrorEglises] = useState<string | null>(null);
  const [selectedEglise, setSelectedEglise] = useState<Eglise | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Consolidated date state to reduce re-renders
  const [dateState, setDateState] = useState<{
    day: number | null;
    month: number | null;
    year: number | null;
    loading: boolean;
    error: string | null;
  }>({ day: null, month: null, year: null, loading: true, error: null });

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch user eglises
      if (authState.user?.id) {
        setLoadingEglises(true);
        setErrorEglises(null);
        try {
          const fetchedEglises = await getUserEglises(authState.user.id);
          setEglises(fetchedEglises);
        } catch (err: any) {
          setErrorEglises(err.response?.data?.detail || 'Failed to fetch user eglises.');
        } finally {
          setLoadingEglises(false);
        }
      } else {
        setLoadingEglises(false);
      }

      // Fetch current date (consolidated state update)
      try {
        const dateRes = await getCurrentDate();
        setDateState({
          day: dateRes.date.day,
          month: dateRes.date.month,
          year: dateRes.date.year,
          loading: false,
          error: null,
        });
      } catch (err) {
        // Error handled by notification system
        setDateState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to fetch current date. See console for details.',
        }));
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.user?.id]);

  // Fetch user avatar
  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const profileData = await authService.getMyProfile();
        setAvatarUrl(profileData.profile?.avatar_url || null);
      } catch (err) {
        // Error handled by notification system
      }
    };
    fetchAvatar();
  }, []);

  // Memoized handlers to prevent unnecessary re-renders
  const handleEgliseClick = useCallback((eglise: Eglise) => {
    setSelectedEglise(eglise);
  }, []);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  }, []);

  // Memoized computed values
  const { totalChurches, adminChurches } = useMemo(() => ({
    totalChurches: eglises.length,
    adminChurches: eglises.filter(e => e.is_admin).length,
  }), [eglises]);

  if (dateState.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (dateState.error) {
    return <Alert severity="error">{dateState.error}</Alert>;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Welcome Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 2,
          p: 3,
          mb: 3,
          border: '1px solid #e2e8f0'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            src={avatarUrl || undefined}
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              bgcolor: '#f1f5f9',
              fontSize: '1.5rem',
              fontWeight: 600,
              color: '#64748b'
            }}
          >
            {authState.user?.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#1e293b">
              {t('dashboard.title')}, {authState.user?.name || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {dateState.day && dateState.month && dateState.year && (
                `${dateState.day}/${dateState.month}/${dateState.year}`
              )}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={4}>
          <Box sx={{
            bgcolor: 'white',
            p: 2,
            borderRadius: 1.5,
            border: '1px solid #e2e8f0'
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <ChurchIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h4" fontWeight={700} color="#1e293b">{totalChurches}</Typography>
                <Typography variant="caption" color="text.secondary">{t('dashboard.yourChurches')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{
            bgcolor: 'white',
            p: 2,
            borderRadius: 1.5,
            border: '1px solid #e2e8f0'
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AdminPanelSettingsIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h4" fontWeight={700} color="#1e293b">{adminChurches}</Typography>
                <Typography variant="caption" color="text.secondary">{t('dashboard.admin')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{
            bgcolor: 'white',
            p: 2,
            borderRadius: 1.5,
            border: '1px solid #e2e8f0'
          }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PeopleIcon sx={{ fontSize: 20, color: '#64748b' }} />
              <Box>
                <Typography variant="h4" fontWeight={700} color="#1e293b">{totalChurches - adminChurches}</Typography>
                <Typography variant="caption" color="text.secondary">{t('dashboard.member')}</Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', px: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                minHeight: 48,
                px: 2,
                color: '#64748b',
                fontSize: '0.875rem',
                '&.Mui-selected': { color: '#2563eb', fontWeight: 600 },
              },
              '& .MuiTabs-indicator': { height: 2, bgcolor: '#2563eb' },
            }}
          >
            <Tab icon={<DashboardIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('dashboard.tabs.dashboard')} />
            <Tab icon={<CalendarMonthIcon sx={{ fontSize: 20 }} />} iconPosition="start" label={t('dashboard.tabs.calendar')} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* Section Title */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                {t('dashboard.yourChurches')}
              </Typography>
              <Chip label={`${totalChurches} église(s)`} size="small" sx={{ bgcolor: '#dbeafe', color: '#2563eb', fontWeight: 600 }} />
            </Stack>

            {errorEglises && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{errorEglises}</Alert>}

            {loadingEglises ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
                <CircularProgress />
              </Box>
            ) : eglises.length > 0 ? (
              <Grid container spacing={3}>
                {eglises.map((eglise) => (
                  <Grid item xs={12} sm={6} md={4} key={eglise.id}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 1.5,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.2s ease-in-out',
                        overflow: 'hidden',
                        '&:hover': {
                          borderColor: '#2563eb',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <CardActionArea onClick={() => handleEgliseClick(eglise)} sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                        {/* Church Image/Logo */}
                        {eglise.logo_url || eglise.cover_image_url ? (
                          <Box
                            sx={{
                              height: 120,
                              backgroundImage: `url(${eglise.cover_image_url || eglise.logo_url})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              position: 'relative',
                            }}
                          >
                            {eglise.logo_url && eglise.cover_image_url && (
                              <Avatar
                                src={eglise.logo_url}
                                sx={{
                                  position: 'absolute',
                                  bottom: -20,
                                  left: 16,
                                  width: 48,
                                  height: 48,
                                  border: '3px solid white',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              height: 100,
                              background: eglise.is_admin
                                ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)'
                                : 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <ChurchIcon sx={{ fontSize: 40, color: 'rgba(255,255,255,0.5)' }} />
                          </Box>
                        )}
                        <CardContent sx={{ p: 3, pt: eglise.logo_url && eglise.cover_image_url ? 4 : 3, flexGrow: 1 }}>
                          {/* Church Header */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }} noWrap>
                              {eglise.nom}
                            </Typography>
                          </Box>

                          {/* Status Badge */}
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            {eglise.is_admin ? (
                              <Chip
                                icon={<AdminPanelSettingsIcon sx={{ fontSize: 16 }} />}
                                label={t('dashboard.admin')}
                                size="small"
                                sx={{
                                  bgcolor: '#dcfce7',
                                  color: '#16a34a',
                                  fontWeight: 600,
                                  '& .MuiChip-icon': { color: '#16a34a' },
                                }}
                              />
                            ) : (
                              <Chip
                                label={t('dashboard.member')}
                                size="small"
                                sx={{ bgcolor: '#f1f5f9', color: '#64748b', fontWeight: 500 }}
                              />
                            )}
                            <Tooltip title="Voir détails">
                              <IconButton size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}>
                                <ArrowForwardIcon sx={{ fontSize: 18, color: '#64748b' }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  bgcolor: '#f8fafc',
                  borderRadius: 3,
                  border: '2px dashed #e2e8f0',
                }}
              >
                <ChurchIcon sx={{ fontSize: 64, color: '#94a3b8', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('dashboard.noChurchFound')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.noChurchMember')}
                </Typography>
              </Box>
            )}

            {/* Selected Church Details */}
            {selectedEglise && (
              <Card
                sx={{
                  mt: 4,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                  color: 'white',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    {t('dashboard.selectedChurch')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.name')}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEglise.nom}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>ID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEglise.id}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>{t('dashboard.status')}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {selectedEglise.is_admin ? t('dashboard.administrator') : t('dashboard.member')}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1} keepMounted>
          {dateState.day && dateState.month && dateState.year && (
            <CalendarView initialDay={dateState.day} initialMonth={dateState.month} initialYear={dateState.year} />
          )}
        </TabPanel>
      </Card>
    </Box>
  );
};

export default memo(Dashboard);